import React, { useEffect, useState } from "react"
import { Row, Col, Table } from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import ReactPaginate from "react-paginate"
import { Link } from "react-router-dom"
import MetaTags from "react-meta-tags"
import axios from "axios"
import LoadingSpinner from "../Reports/EditReports/EditTables/loader"
import { useDispatch, useSelector } from "react-redux"
import { getNewOperators } from "store/old/operator/actions"
import jwt from "jsonwebtoken"
import {
  useGetEnptReportsQuery,
  useDeleteReportMutation,
} from "store/enptReport/enptReport.api"
import * as urls from "helpers/url_helper"

const FilteredEnptReports = ({
  filters,
  onFilterChange,
  setLoading,
  loading,
  customers = [],
  operators = [],
  selectedReportId,
  onSelectReport,
  onRegisterRefetch,
}) => {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(0)
  const [reports, setReports] = useState([])
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [downloadingId, setDownloadingId] = useState(null)
  // Decode JWT
  const decodeToken = token => {
    try {
      return jwt.decode(token)
    } catch (error) {
      console.error("Error decoding token:", error)
      return null
    }
  }

  const token = localStorage.getItem("authUser")
  const decodedToken = token ? decodeToken(JSON.parse(token)) : null
  const role = decodedToken?.role || 0

  const itemsPerPage = 5
  const offset = currentPage * itemsPerPage

  // Get operators from Redux store to ensure they're updated when customer changes
  const operatorsRaw = useSelector(state => state.operator.operators || [])
  const filteredOperators = operatorsRaw.map(obj => ({
    id: obj.id,
    label: obj.operator_name + " " + obj.country,
    value: obj.id,
  }))

  // Build Select-friendly option lists
  const customerOptions = [{ value: "", label: "All Customers" }, ...customers]

  const operatorOptions = [
    { value: "", label: "All Operators" },
    ...filteredOperators,
  ]

  // RTK Query hooks
  const { data, isLoading, isError, refetch, isFetching } = useGetEnptReportsQuery({
    page: currentPage + 1,
    limit: itemsPerPage,
    customer: filters.customer,
    operator: filters.operator,
    date: filters.date,
  })

  const [deleteReport] = useDeleteReportMutation()

  const getOperatorFullName = (report) => {
    if (!report) return "";
    const operatorObj = operatorsRaw.find(op => op.id === report.internal_operator_id);
    if (operatorObj && operatorObj.country && report.operator && !report.operator.includes(operatorObj.country)) {
      return `${report.operator} ${operatorObj.country}`;
    }
    return report.operator || "";
  };

  useEffect(() => {
    if (data) {
      console.log("Data received:", data)
      setReports(data.reports || [])
      setTotalPages(data.totalPages || 1)
      setTotalItems(data.totalItems || 0)
      setLoading(false)
    }
  }, [data, setLoading])

  // Load initial operators
  useEffect(() => {
    dispatch(getNewOperators())
  }, [dispatch])

  // Register refetch with parent so they can trigger a list refresh from outside
  useEffect(() => {
    if (onRegisterRefetch) onRegisterRefetch(refetch);
  }, [refetch, onRegisterRefetch])

  // Refetch when filters change and handle customer-operator dependency
  useEffect(() => {
    setCurrentPage(0) // Reset to first page when filters change
    refetch()
  }, [filters.customer, filters.operator, filters.date, refetch])

  const handlePageChange = selectedPage => {
    setCurrentPage(selectedPage.selected)
  }

  const handleCustomerChange = selected => {
    const customerId = selected?.value || ""

    // Update customer filter
    onFilterChange("customer", customerId)

    // Reset operator when customer changes
    onFilterChange("operator", "")

    // Fetch operators for the selected customer
    if (customerId) {
      dispatch(getNewOperators({ customer_id: customerId }))
    } else {
      // If no customer selected, get all operators
      dispatch(getNewOperators())
    }
  }

  const handleOperatorChange = selected => {
    onFilterChange("operator", selected?.value || "")
  }

  const handleDelete = async reportId => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#34c38f",
      cancelButtonColor: "#f46a6a",
      confirmButtonText: "Yes, delete it!",
    })

    if (result.isConfirmed) {
      try {
        setLoading(true)
        await deleteReport(reportId).unwrap()
        Swal.fire("Deleted!", "The report has been deleted.", "success")
        refetch()
      } catch (error) {
        console.error("Error deleting report:", error)
        Swal.fire(
          "Error!",
          "Failed to delete the report. Please try again.",
          "error"
        )
      } finally {
        setLoading(false)
      }
    }
  }

  const handleDownload = async (id, name) => {
    setDownloadingId(id)
    console.log(`[Download] Starting axios download for report id=${id}`);
    try {
      const headers = {
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        responseType: "arraybuffer",
      }
      
      const res = await axios.get(`${urls.DOWNLOAD_ENPT_REPORT}/${id}`, headers)
      const contentType = "application/octet-stream"
      const blob = new Blob([res.data], { type: contentType })
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = name ? `${name}.xlsx` : `EnptReport_${id}.xlsx`
      link.click()
      
      console.log(`[Download] Done — file triggered for download.`);
    } catch (error) {
      console.error("[Download] Error:", error)
      Swal.fire(
        "Error!",
        "Failed to download the report. Please try again.",
        "error"
      )
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <React.Fragment>
      <MetaTags>
        <title>ENPT Reports</title>
      </MetaTags>

      <div className="pb-4">
        {isError && (
          <div className="alert alert-danger">
            Failed to load reports. Please try again later.
          </div>
        )}

        <Row>
          <Col md="12">
            <div>
              {/* Filters */}
              <div className="mb-4">
                <Row style={{ zIndex: "2", position: "relative" }}>
                  <Col md="2">
                    <h5 style={{ paddingTop: "10px" }}>Filters</h5>
                  </Col>
                  <Col md="2">
                    <Select
                      classNamePrefix="select"
                      isSearchable
                      name="customer"
                      placeholder="Customer"
                      value={customerOptions.find(
                        option => option.value === filters.customer
                      )}
                      options={customerOptions}
                      onChange={handleCustomerChange}
                    />
                  </Col>
                  <Col md="2">
                    <Select
                      classNamePrefix="select"
                      isSearchable
                      name="operator"
                      placeholder="Operator"
                      value={operatorOptions.find(
                        option => option.value === filters.operator
                      )}
                      options={operatorOptions}
                      onChange={handleOperatorChange}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col className="col-12">
                    <div className="col-12 scrollable">
                      <Table className="table-responsive align-middle mb-0 report_table_edit">
                        <thead>
                          <tr>
                            <th>Report ID</th>
                            <th>Report Name</th>
                            <th>Customer</th>
                            <th>Operator</th>
                            <th>Tested Date</th>
                            {([2, 3, 4, 5].includes(role)) && <th className="ps-4">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {loading || isLoading || isFetching ? (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <LoadingSpinner />
                              </td>
                            </tr>
                          ) : reports.length > 0 ? (
                            reports.map((report, index) => (
                              <tr 
                                key={report.id || index}
                                onClick={() => onSelectReport && onSelectReport(report)}
                                style={{ 
                                  cursor: "pointer", 
                                  backgroundColor: selectedReportId === report.id ? "#e8f0fe" : "transparent"
                                }}
                                className={selectedReportId === report.id ? "table-active" : ""}
                              >
                                <td>
                                  <div className="form-check d-flex align-items-center">
                                    <input
                                      type="radio"
                                      className="form-check-input mt-0"
                                      checked={selectedReportId === report.id}
                                      onChange={() => onSelectReport && onSelectReport(report)}
                                      style={{ width: "14px", height: "14px" }}
                                    />
                                    <label className="form-check-label ms-2 mb-0" title={report.id} style={{ fontSize: "13px" }}>
                                      {report.id
                                        ? report.id.toString().length > 8
                                          ? `${report.id.toString().substring(0, 8)}...`
                                          : report.id
                                        : offset + index + 1}
                                    </label>
                                  </div>
                                </td>
                                <td>
                                  {report.name ||
                                    `${report.customer} - ${getOperatorFullName(report)}_${
                                      report.test_date?.slice(0, 10) || (report.createdAt ? new Date(report.createdAt).toISOString().slice(0, 10) : "N/A")
                                    }`}
                                </td>
                                <td>{report.customer}</td>
                                <td>{getOperatorFullName(report)}</td>
                                <td>
                                  {report.test_date
                                    ? new Date(report.test_date)
                                        .toISOString()
                                        .slice(0, 10)
                                    : report.createdAt
                                    ? new Date(report.createdAt)
                                        .toISOString()
                                        .slice(0, 10)
                                    : "N/A"}
                                </td>
                                  {[2, 3, 4, 5].includes(role) && (
                                    <td>
                                      <div className="d-flex ps-4 gap-2">
                                        <Link
                                          className="text-success"
                                          to="#"
                                          title="Download Excel Report"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (downloadingId === report.id) return;
                                            handleDownload(
                                              report.id,
                                              report.name || `ENPT Report - ${report.customer} - ${getOperatorFullName(report)}`
                                            );
                                          }}
                                        >
                                          {downloadingId === report.id ? (
                                            <div className="spinner-border text-success" role="status" style={{ width: '20px', height: '20px', marginTop: '2px' }}>
                                              <span className="visually-hidden">Downloading...</span>
                                            </div>
                                          ) : (
                                            <i className="bx bx-down-arrow-alt font-size-24"></i>
                                          )}
                                        </Link>
                                        {role === 5 && (
                                          <Link
                                            className="text-danger"
                                            to="#"
                                            title="Delete Report"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleDelete(report.id);
                                            }}
                                            style={{ cursor: "pointer" }}
                                          >
                                            <i className="bx bx-x font-size-24"></i>
                                          </Link>
                                        )}
                                      </div>
                                    </td>
                                  )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                No Data Found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="col-12">
                        <ReactPaginate
                          previousLabel={"previous"}
                          nextLabel={"next"}
                          forcePage={currentPage}
                          breakLabel={"..."}
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName={"pagination"}
                          activeClassName={"active"}
                        />
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  )
}

export default FilteredEnptReports
